/* eslint-disable react-refresh/only-export-components */
/**
 * Platform Routes
 *
 * Top-level authenticated routes that are not workspace- or admin-scoped.
 * Includes: Actions (primary landing), Home (redirect), Help, Command Center,
 * Signal Analytics, and the OAuth callbacks.
 */

import { lazy, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";

import { AuthGate } from "@/components/access/AuthGate";

// Platform-level shell for non-workspace pages
const PlatformShell = lazy(() => import("@/components/shell2/PlatformShell"));

// Actions Page (primary landing - Nexus Agency product center)
const ActionsPage = lazy(() => import("@/pages/ActionsPage"));

// Platform-level Help page (new navigation)
const HelpPage = lazy(() => import("@/pages/HelpPage"));

// Weekly Command Center (platform-level, user-scoped)
const DecisionCockpitPage = lazy(() => import("@/pages/DecisionCockpitPage"));

// Prepared Action Review (platform-level, full-screen review surface)
const PreparedActionReviewPage = lazy(
  () => import("@/pages/PreparedActionReviewPage"),
);

// Signal Analytics (Intelligence Engine v0.5.0)

// OAuth callbacks
const CCOAuthCallback = lazy(() => import("@/pages/oauth/CCOAuthCallback"));
const ClickUpOAuthCallback = lazy(
  () => import("@/pages/oauth/ClickUpOAuthCallback"),
);
const AuthCallbackPage = lazy(() => import("@/pages/AuthCallbackPage"));
// Supabase OAuth 2.1 server consent screen (Nexus MCP connector)
const OAuthConsentPage = lazy(() => import("@/pages/oauth/OAuthConsentPage"));

// Story Collector (public-facing, no shell/sidebar — standalone page)
const StoryCollector = lazy(() =>
  import("@/features/stories/StoryCollector").then((m) => ({
    default: m.StoryCollector,
  })),
);

const GivingDayCommandCenterPage = lazy(() =>
  import("@/features/giving-day").then((m) => ({
    default: m.GivingDayCommandCenterPage,
  })),
);

const ProjectBoardPage = lazy(() =>
  import("@/features/project-board").then((m) => ({
    default: m.ProjectBoardPage,
  })),
);
const OpportunityBoardPage = lazy(() =>
  import("@/features/opportunity-board").then((m) => ({
    default: m.OpportunityBoardPage,
  })),
);

const PageLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="text-gray-600">Loading...</div>
  </div>
);

export function platformRoutes() {
  return (
    <>
      {/* OAuth server consent (Nexus MCP connector) — sign-in required */}
      <Route
        path="/oauth/consent"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <OAuthConsentPage />
            </Suspense>
          </AuthGate>
        }
      />

      {/* Auth callback (public) */}
      <Route
        path="/auth/callback"
        element={
          <Suspense fallback={<PageLoader />}>
            <AuthCallbackPage />
          </Suspense>
        }
      />

      {/* =================================================================== */}
      {/* ACTIONS - Primary landing page (Nexus Agency product center)       */}
      {/* =================================================================== */}

      <Route
        path="actions"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <ActionsPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />

      {/* =================================================================== */}
      {/* HOME - Redirects to Actions (Nexus Agency: actions-first model)     */}
      {/* Legacy: /home was "Agency Dashboard" but now Actions is primary     */}
      {/* =================================================================== */}

      <Route
        path="home"
        element={
          <AuthGate>
            <Navigate to="/actions" replace />
          </AuthGate>
        }
      />

      {/* =================================================================== */}
      {/* PLATFORM-LEVEL HELP - Available to all authenticated users         */}
      {/* =================================================================== */}

      <Route
        path="help"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <HelpPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />

      {/* =================================================================== */}
      {/* DECISION COCKPIT - User-scoped weekly brief (cross-client)          */}
      {/* Renamed from "Command Center" 2026-05-18 — see                      */}
      {/* docs/superpowers/specs/2026-05-18-command-center-naming-collision-design.md */}
      {/* /command-center kept as a permanent client-side redirect            */}
      {/* =================================================================== */}

      <Route
        path="cockpit"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <DecisionCockpitPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />

      <Route
        path="command-center"
        element={<Navigate to="/cockpit" replace />}
      />

      {/* =================================================================== */}
      {/* PREPARED ACTION REVIEW - Full-screen review surface                 */}
      {/* Scaffold (Task 8.1); regions filled in Tasks 8.2–8.4                */}
      {/* =================================================================== */}

      <Route
        path="command/review/:preparedActionId"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PreparedActionReviewPage />
            </Suspense>
          </AuthGate>
        }
      />

      {/* =================================================================== */}
      {/* OAUTH CALLBACKS - Handle third-party OAuth redirects               */}
      {/* =================================================================== */}

      <Route
        path="oauth/callback/constant-contact"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <CCOAuthCallback />
            </Suspense>
          </AuthGate>
        }
      />

      <Route
        path="oauth/callback/clickup"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <ClickUpOAuthCallback />
            </Suspense>
          </AuthGate>
        }
      />

      {/* =================================================================== */}
      {/* STORY COLLECTOR - Public-facing story intake for nonprofit staff    */}
      {/* No auth, no shell, no sidebar — standalone conversational tool      */}
      {/* =================================================================== */}

      <Route
        path="stories/:clientSlug/:variantSlug?"
        element={
          <Suspense fallback={<PageLoader />}>
            <StoryCollector />
          </Suspense>
        }
      />
      <Route
        path="stories/the-center-2"
        element={<Navigate to="/stories/tcipc" replace />}
      />

      {/* =================================================================== */}
      {/* GIVING DAYS - Cross-client seasonal command center                  */}
      {/* Authorization is enforced by RLS: the loader returns only the        */}
      {/* participants the caller may see. This route adds no exception.       */}
      {/* =================================================================== */}

      {/* =================================================================== */}
      {/* OPPORTUNITY BOARD - Internal Track15 strategy board                */}
      {/* Authorization is enforced by RLS (staff_users only); this route   */}
      {/* adds no exception.                                                 */}
      {/* =================================================================== */}

      <Route
        path="projects"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <ProjectBoardPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />

      <Route
        path="opportunities"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <OpportunityBoardPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />

      <Route
        path="giving-days/:eventFamily/:eventYear"
        element={
          <AuthGate>
            <Suspense fallback={<PageLoader />}>
              <PlatformShell>
                <GivingDayCommandCenterPage />
              </PlatformShell>
            </Suspense>
          </AuthGate>
        }
      />
    </>
  );
}
